/* Attendance grid table — all cell-level interactions live here:
   click-to-mark, shift-click multi-select, Excel-style drag-fill,
   keyboard hotkeys, arrow navigation, and Ctrl+C/V copy-paste. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../../../components/ui/card';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../../components/ui/dropdown-menu';
import { dowShort, dayNum, cellKey } from './attendanceUtils';

export const AttendanceGrid = ({
  data, loading, canEdit, statusMap, activeStatuses, hotkeyMap,
  selection, isSelected, toggleSelection, clearSelection,
  markOne, markColumn, markRow, bulkApply, clearCells, openRemarkEditor,
}) => {
  // In-app clipboard: last copied cell status. `undefined` = nothing copied
  // yet; `null` = an explicitly copied empty mark.
  const clipboardRef = useRef(undefined);
  // Excel-style fill drag: {sourceStatus, anchor:{r,c}, end:{r,c}}
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(drag);
  dragRef.current = drag;
  // Live shift-key state — Radix opens on pointerdown, so onClick's
  // e.shiftKey fires too late for the multi-select toggle.
  const shiftKeyRef = useRef(false);
  // cellKey of the one cell whose picker menu is open (controlled).
  const [openCell, setOpenCell] = useState(null);

  useEffect(() => {
    const dn = (e) => { if (e.key === 'Shift') shiftKeyRef.current = true; };
    const up = (e) => { if (e.key === 'Shift') shiftKeyRef.current = false; };
    const blur = () => { shiftKeyRef.current = false; };
    window.addEventListener('keydown', dn);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', dn);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, []);

  // Cells inside the current drag rect (inclusive).
  const dragCells = useMemo(() => {
    if (!drag || !drag.end) return null;
    const r1 = Math.min(drag.anchor.r, drag.end.r);
    const r2 = Math.max(drag.anchor.r, drag.end.r);
    const c1 = Math.min(drag.anchor.c, drag.end.c);
    const c2 = Math.max(drag.anchor.c, drag.end.c);
    const keys = new Set();
    for (let ri = r1; ri <= r2; ri++) {
      const row = data.rows[ri];
      if (!row) continue;
      for (let ci = c1; ci <= c2; ci++) {
        const date = data.dates[ci];
        if (date) keys.add(cellKey(row.trainee_id, date));
      }
    }
    return keys;
  }, [drag, data]);

  // Global mouseup ends the drag-fill and applies the source status.
  useEffect(() => {
    if (!drag) return;
    const onUp = () => {
      const current = dragRef.current;
      if (current?.sourceStatus && dragCells && dragCells.size > 1) {
        bulkApply(dragCells, current.sourceStatus);
      }
      setDrag(null);
    };
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, [drag, dragCells, bulkApply]);

  const focusCell = (traineeId, date) => {
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="attendance-cell-${traineeId}-${date}"]`);
      el?.focus?.();
    });
  };

  const handleCellKeyDown = (e, rowIdx, colIdx, cell, trainee) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      clearSelection();
      setDrag(null);
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
      e.preventDefault();
      clipboardRef.current = cell.status ?? null;
      toast.success(cell.status
        ? `Copied ${statusMap[cell.status]?.name || cell.status}`
        : 'Copied empty mark');
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
      e.preventDefault();
      const code = clipboardRef.current;
      if (code === undefined) {
        toast.info('Nothing to paste — press Ctrl+C on a cell first');
        return;
      }
      if (selection.size > 0) {
        if (code === null) clearCells(selection);
        else bulkApply(selection, code);
      } else {
        markOne(trainee.trainee_id, cell.date, code);
      }
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight'
       || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const dx = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
      const dy = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
      const nextCol = Math.max(0, Math.min(data.dates.length - 1, colIdx + dx));
      const nextRow = Math.max(0, Math.min(data.rows.length - 1, rowIdx + dy));
      const nt = data.rows[nextRow];
      const nd = data.dates[nextCol];
      if (nt && nd) focusCell(nt.trainee_id, nd);
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      if (cell.status) markOne(trainee.trainee_id, cell.date, null);
      return;
    }
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const code = hotkeyMap[e.key.toLowerCase()];
      if (code) {
        e.preventDefault();
        markOne(trainee.trainee_id, cell.date, code);
      }
    }
  };

  return (
    <Card className="overflow-x-auto" data-testid="attendance-grid-card">
      {loading ? (
        <p className="p-8 text-center text-sm text-muted-foreground">Loading grid…</p>
      ) : data.rows.length === 0 ? (
        <p className="p-8 text-center text-sm text-muted-foreground" data-testid="attendance-empty-state">
          No trainees in this batch / window.
        </p>
      ) : (
        <table className="text-sm border-collapse" data-testid="attendance-grid">
          <thead>
            <tr className="border-b">
              <th className="sticky left-0 bg-background p-2 text-left z-10 min-w-[180px]">Trainee</th>
              {data.dates.map(d => (
                <th key={d} className="p-1 text-center border-l min-w-[36px]">
                  {canEdit ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="font-display text-xs leading-tight hover:bg-muted rounded px-1 py-0.5"
                                data-testid={`attendance-col-header-${d}`}>
                          <div className="text-muted-foreground">{dowShort(d)}</div>
                          <div className="font-bold">{dayNum(d)}</div>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {activeStatuses.map(s => (
                          <DropdownMenuItem key={s.code} onClick={() => markColumn(d, s.code)}
                                            data-testid={`mark-all-${d}-${s.code}`}>
                            Mark all {s.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <div className="font-display text-xs leading-tight">
                      <div className="text-muted-foreground">{dowShort(d)}</div>
                      <div className="font-bold">{dayNum(d)}</div>
                    </div>
                  )}
                </th>
              ))}
              <th className="p-2 text-center border-l min-w-[80px]">Att %</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, rowIdx) => (
              <tr key={r.trainee_id} className="border-b hover:bg-muted/30 group/row"
                  data-testid={`attendance-row-${r.trainee_id}`}>
                <td className="sticky left-0 bg-background p-2 z-10">
                  <div className="flex items-center gap-1">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{r.name}</div>
                      {r.employee_id && (
                        <div className="text-xs text-muted-foreground truncate">{r.employee_id}</div>
                      )}
                    </div>
                    {canEdit && data.dates.length > 0 && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="opacity-0 group-hover/row:opacity-100 transition-opacity p-1 rounded hover:bg-muted"
                            title="Fill entire row"
                            data-testid={`fill-row-${r.trainee_id}`}
                          >
                            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-48">
                          <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                            Fill all {data.dates.length} days
                          </div>
                          {activeStatuses.map(opt => (
                            <DropdownMenuItem
                              key={opt.code}
                              onClick={() => markRow(r.trainee_id, opt.code)}
                              data-testid={`fill-row-${r.trainee_id}-${opt.code}`}
                            >
                              <span className={`inline-block w-4 h-4 rounded text-[9px] font-semibold flex items-center justify-center mr-2 ${opt.color}`}>
                                {opt.short_code}
                              </span>
                              {opt.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </td>
                {r.cells.map((c, colIdx) => {
                  const s = c.status ? statusMap[c.status] : null;
                  const remarkHint = c.remark ? `\n💬 ${c.remark.text} — ${c.remark.updated_by_name || c.remark.created_by_name || ''}` : '';
                  const remarkBadge = c.remark ? (
                    <span
                      onClick={(e) => openRemarkEditor(e, r, c)}
                      className="absolute top-0 right-0 w-0 h-0 border-t-[8px] border-l-[8px] border-t-amber-500 border-l-transparent rounded-tr cursor-pointer"
                      title={`💬 ${c.remark.text} — ${c.remark.updated_by_name || c.remark.created_by_name || ''}`}
                      data-testid={`remark-indicator-${r.trainee_id}-${c.date}`}
                    />
                  ) : null;
                  if (!canEdit) {
                    return (
                      <td key={c.date} className="p-1 text-center border-l">
                        <span className="relative inline-flex">
                          <span
                            className={`inline-flex w-7 h-7 rounded text-[10px] font-bold items-center justify-center ${
                              s ? s.color : 'bg-muted text-muted-foreground'
                            }`}
                            title={(s ? s.name : 'Not marked') + remarkHint}
                            data-testid={`attendance-cell-${r.trainee_id}-${c.date}`}
                          >
                            {s ? s.short_code : '·'}
                          </span>
                          {remarkBadge}
                        </span>
                      </td>
                    );
                  }
                  const cellSelected = isSelected(r.trainee_id, c.date);
                  const inDrag = dragCells?.has(cellKey(r.trainee_id, c.date)) ?? false;
                  const ck = cellKey(r.trainee_id, c.date);
                  return (
                    <td key={c.date} className="p-1 text-center border-l">
                      <div className="relative inline-block group/cell">
                        <DropdownMenu
                          open={openCell === ck}
                          onOpenChange={(nextOpen) => {
                            // Shift held on pointerdown ⇒ multi-select
                            // toggle instead of opening the picker.
                            if (nextOpen && shiftKeyRef.current) {
                              toggleSelection(r.trainee_id, c.date);
                              return;
                            }
                            setOpenCell(nextOpen ? ck : null);
                          }}
                        >
                          <DropdownMenuTrigger asChild>
                            <button
                              onMouseEnter={() => {
                                if (dragRef.current) {
                                  setDrag(d => d ? ({ ...d, end: { r: rowIdx, c: colIdx } }) : d);
                                }
                              }}
                              onKeyDown={(e) => handleCellKeyDown(e, rowIdx, colIdx, c, r)}
                              onContextMenu={(e) => openRemarkEditor(e, r, c)}
                              className={`w-7 h-7 rounded text-[10px] font-bold transition hover:scale-110 cursor-pointer ${
                                s ? s.color : 'bg-muted hover:bg-muted-foreground/20'
                              } ${cellSelected ? 'ring-2 ring-amber-500 ring-offset-1' : ''}
                                 ${inDrag && !cellSelected ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`}
                              title={(s ? `${s.name} — click to change, right-click to comment` : 'Not marked — click to set') + remarkHint}
                              data-testid={`attendance-cell-${r.trainee_id}-${c.date}`}
                            >
                              {s ? s.short_code : '·'}
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="center" className="w-44">
                            {activeStatuses.map(opt => (
                              <DropdownMenuItem
                                key={opt.code}
                                onClick={() => markOne(r.trainee_id, c.date, opt.code)}
                                data-testid={`attendance-pick-${r.trainee_id}-${c.date}-${opt.code}`}
                                className={c.status === opt.code ? 'bg-muted' : ''}
                              >
                                <span className={`inline-block w-4 h-4 rounded text-[9px] font-semibold flex items-center justify-center mr-2 ${opt.color}`}>
                                  {opt.short_code}
                                </span>
                                {opt.name}
                              </DropdownMenuItem>
                            ))}
                            {c.status && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={(e) => { setOpenCell(null); openRemarkEditor(e, r, c); }}
                                  data-testid={`attendance-comment-${r.trainee_id}-${c.date}`}
                                >
                                  <MessageSquare className="w-3.5 h-3.5 mr-2 text-amber-600" />
                                  {c.remark ? 'Edit comment' : 'Add comment'}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => markOne(r.trainee_id, c.date, null)}
                                  data-testid={`attendance-clear-${r.trainee_id}-${c.date}`}
                                  className="text-muted-foreground"
                                >
                                  Clear mark
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        {remarkBadge}
                        {s && (
                          <span
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDrag({
                                sourceStatus: s.code,
                                anchor: { r: rowIdx, c: colIdx },
                                end: { r: rowIdx, c: colIdx },
                              });
                            }}
                            className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-foreground rounded-sm opacity-0 group-hover/cell:opacity-100 cursor-crosshair shadow"
                            title={`Drag to fill ${s.name}`}
                            data-testid={`fill-handle-${r.trainee_id}-${c.date}`}
                          />
                        )}
                      </div>
                    </td>
                  );
                })}
                <td className="p-2 text-center border-l">
                  <span className={`font-display tabular-nums font-bold ${
                    r.attendance_pct < 80 && r.total_working_days > 0 ? 'text-rose-600' : ''
                  }`}>
                    {r.total_working_days > 0 ? `${r.attendance_pct}%` : '—'}
                  </span>
                  <div className="text-xs text-muted-foreground">
                    {r.total_attendance}/{r.total_working_days}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
};
